
import { mixins } from 'vue-class-component';
import CampaignWidgetMixin from '@/mixins/campaignWidget';
import WithCampaignConstants from '@/mixins/WithCampaignConstants';
import { Redemption, RedemptionResult } from '../../api/models/redemption';
import { ShopifySubmission } from '../../api/models/submission';

export default class MRedemptionSelection extends mixins(
  WithCampaignConstants,
  CampaignWidgetMixin
) {
  productToVariants: any = {};
  selectedProductIdx = 0;
  selectedOptions: string[] = [];
  openOptionDropdown: boolean[];
  submissionDisabled = true;
  quantityCount = 1;
  maxQuantityCount: number | undefined = undefined;
  isSubmittingRedemption = false;

  /** timer for the activeRedemption if campaign action type = SHOPIFY_DELAYED */
  activeRedemptionTimer: NodeJS.Timer | null = null;
  isMounted = false;

  // Vue lifecycle hooks
  async created() {
    if (this.modifyingSelection) {
      // came from modify request => set options accordingly
      const productId = (
        this.state.activeRedemption.submissions![0] as ShopifySubmission
      ).productId.toString();
      // NOTE: variantId is of type string
      // @ts-ignore
      const { variantId, quantity } = this.state.activeRedemption.submissions![0];
      this.quantityCount = quantity!;
      this.selectedProductIdx = this.state.shopifyProducts.findIndex(
        (product: any): boolean => product.productId === productId
      );
      const selectedVariant = this.state.shopifyProducts[
        this.selectedProductIdx
      ]!.productData.variants.find((variant: any): boolean => variant.id.toString() === variantId);
      this.selectedOptions = selectedVariant.title.split(' / ');
    }
    this.setUpProductOptions();
  }

  async mounted() {
    this.isMounted = true;
  }

  beforeUnmount() {
    this.selectedProductIdx = 0;
    this.isMounted = false;
    if (this.activeRedemptionTimer) {
      clearInterval(this.activeRedemptionTimer);
      this.activeRedemptionTimer = null;
    }
  }

  // Vue lifecycle hooks ends
  // component methods
  async goBack() {
    this.clearActiveTokenAndRedemption();
  }

  /** Computed prop depending on `this.selectedOptions` */
  get selectedVariantKey(): string {
    // shopify variants are formed by concatenating the options with ' / '
    return this.selectedOptions.join(' / ');
  }

  get modifyingSelection(): boolean {
    if (!this.state.activeRedemption) {
      return false;
    }
    // sanity check -- make sure has redeemed (remaining === 0), tokenId and contract === redemption details
    const { activeRedemption: redemption, activeEligibleToken: token } = this.state;
    if (
      token!.remaining > 0 ||
      token?.network !== redemption.network ||
      token?.contractAddress !== redemption.contractAddress ||
      token?.tokenId !== redemption.tokenId
    ) {
      this.$store.commit(`${this.mmid}/setActiveRedemption`, null, { root: true });
      return false;
    }

    return true;
  }

  // product methods
  selectProduct(idx: number) {
    this.selectedProductIdx = idx;
    this.resetSelectedProductOptions();
    this.setUpProductOptions();
  }

  incrementQuantityCount() {
    if (this.maxQuantityCount && this.quantityCount < this.maxQuantityCount) {
      this.quantityCount += 1;
    }
  }

  decrementQuantityCount() {
    if (this.quantityCount > 1) {
      // can't be less than 1
      this.quantityCount -= 1;
    }
  }

  async submitSelection(_submitEvent: any) {
    this.isSubmittingRedemption = true;
    try {
      let submissions: ShopifySubmission[] = [];

      const selectedVariant =
        this.productToVariants[this.selectedProductIdx][this.selectedVariantKey];
      const selectedProduct = this.state.shopifyProducts[this.selectedProductIdx];
      const submission = {
        shop: selectedProduct.shop,
        productId: selectedProduct.productId,
        variantId: selectedVariant
          ? selectedVariant.id
          : this.state.shopifyProducts[this.selectedProductIdx].productData.variants[0].id,
        quantity: this.quantityCount
      };

      submissions.push(submission);

      const { network, contractAddress, tokenId } = this.state.activeEligibleToken!;

      const redemption = new Redemption(network, contractAddress, tokenId, submissions, '', '');
      const oauthToken = await this.getOAuth();
      const res = RedemptionResult.fromJson(
        await this.state.RedeemAPIClient.submitRedemption(
          oauthToken!,
          parseInt(this.mmid!),
          redemption,
          this.modifyingSelection ? this.state.activeRedemption.id : undefined
        )
      );
      this.setRedemptionDetails(res);

      if (this.state.campaignActionType === this.CampaignAction.SHOPIFY_DELAYED) {
        this.$store.commit(
          `${this.mmid}/setShopifyQueue`,
          await this.state.RedeemAPIClient.getShopifyQueue(oauthToken!, parseInt(this.mmid!)),
          { root: true }
        );

        // check active redemption state every 5 s
        this.activeRedemptionTimer = setInterval(async () => {
          if (!this.isMounted) {
            return;
          }
          const redemptions = await this.state.RedeemAPIClient.getRedemptions(
            oauthToken!,
            parseInt(this.mmid!)
          );
          this.$store.commit(
            `${this.mmid}/setActiveRedemption`,
            redemptions.find(
              (redemption: RedemptionResult) => redemption.id == this.state.activeRedemption!.id
            ),
            { root: true }
          );
        }, 5000);
      }
      await this.updateState(true);
    } catch {
      alert('Error encountered during submission. Please try again.');
    } finally {
      this.isSubmittingRedemption = false;
    }
  }

  // product option methods
  resetSelectedProductOptions() {
    this.selectedOptions = [];
  }

  setUpProductOptions() {
    const product = this.state.shopifyProducts[this.selectedProductIdx];
    this.maxQuantityCount = product.limit;
    const productOptionCount = product.productData.options.length;
    this.openOptionDropdown = new Array(productOptionCount);

    if (productOptionCount > 0) {
      // enable submission if stock is unlimited or the product still has stock
      if (!product.total || product.total - product.consumed > 0) {
        this.submissionDisabled = false;
      } else {
        this.submissionDisabled = true;
      }

      // disable submission if there are multiple product options (if only one, auto-selects the first)
      if (
        (productOptionCount > 1 || product.productData.options[0].values.length > 1) &&
        !this.selectedVariantKey
      ) {
        this.submissionDisabled = true;
      }

      const productVariantKeyToData: any = {};

      product.productData.variants.forEach((variant: any) => {
        productVariantKeyToData[variant.title] = variant;
      });

      this.productToVariants[this.selectedProductIdx] = productVariantKeyToData;
    }
  }

  openOption(optionIdx: number) {
    this.openOptionDropdown[optionIdx] = !this.openOptionDropdown[optionIdx];
    this.$forceUpdate();
  }

  changeOption(id: number, value: string) {
    const product = this.state.shopifyProducts[this.selectedProductIdx];
    const productOptionCount = product.productData.options.length;
    // Vue.set(this.selectedOptions, id, value); // vue2
    this.selectedOptions[id] = value; // vue3

    // TODO: avoid iterating every time we make a selection/optimize this
    // eg: pulling this logic out into a separate component
    for (let i = 0; i < productOptionCount; i++) {
      if (!this.selectedOptions[i]) {
        this.submissionDisabled = true;
        return; // early return
      }
    }
    this.submissionDisabled = false;
  }
}
